/* eslint-disable react/no-array-index-key */
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  styled,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useTheme } from '@mui/styles';
import React, { useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  useBoolean,
  useInterval,
  useLifecycles,
  useWindowSize,
} from 'react-use';

import { fetchAllCharts, fetchHwList, setManagerId } from '../modules/picareDashboard';
import PiCareBarChartHourly from '../components/picare/PiCareBarChartHourly';
import PiCareBarChartUsage from '../components/picare/PiCareBarChartUsage';
import PiCareLineChartUsage from '../components/picare/PiCareLineChartUsage';
import PiCarePerformanceWidget from '../components/picare/PiCarePerformanceWidget';
import PiCarePieChartEmotion from '../components/picare/PiCarePieChartEmotion';
import PiCarePieChartUsage from '../components/picare/PiCarePieChartUsage';
import PiCareReportPreview from '../components/picare/PiCareReportPreview';
import PiCareSupplyTable from '../components/picare/PiCareSupplyTable';

const DEFAULT_REPORT_DATA = {
  header: {
    outline:
      '장기요양 급여제공 기준 및 급여비용 산정방법등에 관한 세부사항 [별지 제 26호서식]',
    title: '프로그램 운영기록지',
    category: '인지기능',
    centerName: '',
  },
  programInfo: {
    name: '',
    group: '',
    type: '',
    date: '',
    author: '',
    director: '',
    location: '',
    instructors: [],
    materials: '',
    goal: '',
    content: '',
    overallEvaluation: '',
    rate: '',
  },
  recoveryTraining: [
    ['신체기능', '훈련'],
    ['기본동작', '훈련'],
    ['일상생활', '동작훈련'],
    ['인지기능', '향상훈련'],
    ['여가, 정서', '프로그램'],
    ['물리치료'],
    ['작업치료'],
    ['인지활동형', '프로그램'],
  ],
  recoveryChecks: [false, false, false, false, false, false, false, false],
  recipients: [],
};

// eslint-disable-next-line no-unused-vars
const TEST_REPORT_DATA = {
  header: {
    outline:
      '장기요양 급여제공 기준 및 급여비용 산정방법등에 관한 세부사항 [별지 제 26호서식]',
    title: '프로그램 운영기록지',
    category: '인지기능',
    centerName: '주간보호센터',
  },
  programInfo: {
    name: '인지기능 향상 프로그램 - 기억력 훈련',
    group: '인지기능 그룹 A',
    type: '[인지] 인지기능 향상훈련',
    date: '2026년 5월 22일 (목) 오전 10:00 ~ 11:00',
    author: '김지수',
    director: '박민준',
    location: '프로그램실 1호',
    instructors: ['이수현', '최예린'],
    materials: '색연필, A4 용지, 기억력 카드게임 세트, 화이트보드',
    goal: '어르신들의 단기 기억력 및 집중력 향상을 목표로 하며, 일상생활 속 인지 능력 유지에 기여한다.',
    content:
      '1. 워밍업: 간단한 숫자 따라 말하기 (5분)\n2. 기억력 카드 매칭 게임 (20분)\n3. 오늘의 날씨·날짜 이야기 나누기 (10분)\n4. 색칠하기 활동으로 집중력 훈련 (20분)\n5. 마무리 체조 및 소감 나누기 (5분)',
    overallEvaluation:
      '대부분의 어르신들이 적극적으로 참여하였으며, 카드 매칭 게임에서 높은 집중력을 보임. 일부 어르신의 경우 반복 설명이 필요하였으나 전반적으로 긍정적인 반응을 보임.',
    rate: '',
  },
  recoveryTraining: [
    ['신체기능', '훈련'],
    ['기본동작', '훈련'],
    ['일상생활', '동작훈련'],
    ['인지기능', '향상훈련'],
    ['여가, 정서', '프로그램'],
    ['물리치료'],
    ['작업치료'],
    ['인지활동형', '프로그램'],
  ],
  recoveryChecks: [false, false, false, true, false, false, false, false],
  recipients: [
    {
      name: '홍길동',
      attended: true,
      scores: { participation: '상', satisfaction: '상', performance: '중' },
      note: '적극적으로 참여, 카드 게임에서 우수한 성과를 보임',
    },
    {
      name: '김순희',
      attended: true,
      scores: { participation: '중', satisfaction: '상', performance: '중' },
      note: '집중력 양호, 반복 설명 시 잘 이해함',
    },
    {
      name: '이정자',
      attended: false,
      scores: { participation: null, satisfaction: null, performance: null },
      note: '',
    },
    {
      name: '박영수',
      attended: true,
      scores: { participation: '상', satisfaction: '중', performance: '상' },
      note: '색칠하기 활동에서 특히 높은 집중력 발휘',
    },
    {
      name: '최말순',
      attended: true,
      scores: { participation: '중', satisfaction: '중', performance: '중' },
      note: '',
    },
    {
      name: '정복순',
      attended: false,
      scores: { participation: null, satisfaction: null, performance: null },
      note: '',
    },
    {
      name: '강태양',
      attended: true,
      scores: { participation: '하', satisfaction: '중', performance: '하' },
      note: '컨디션 저조, 간헐적 참여',
    },
  ],
};

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const DragCardHeader = styled(Typography)(
  ({ theme }) => `
  margin: -1rem -1rem 0.5rem;
  padding: 0.25rem 1rem;
  background-color: ${theme.palette.secondary.main};
  color: ${theme.palette.secondary.contrastText};
  cursor: move;
  font-size: 0.875rem !important;
  font-weight: bold !important;
`,
);

// lg=16col: 상단 3열(5+5+6), 중단 2등분(8+8,h12), 하단 9+7
const LAYOUT = {
  lg: [
    {
      w: 6,
      h: 8,
      x: 0,
      y: 0,
      i: '0',
    },
    {
      w: 4,
      h: 8,
      x: 12,
      y: 0,
      i: '1',
    },
    {
      w: 5,
      h: 14,
      x: 7,
      y: 8,
      i: '2',
    },
    {
      w: 4,
      h: 7,
      x: 12,
      y: 8,
      i: '3',
    },
    {
      w: 7,
      h: 14,
      x: 0,
      y: 8,
      i: '4',
    },
    {
      w: 6,
      h: 8,
      x: 6,
      y: 0,
      i: '5',
    },
    {
      w: 4,
      h: 7,
      x: 12,
      y: 15,
      i: '6',
    },
  ],
  md: [
    {
      w: 5,
      h: 10,
      x: 0,
      y: 0,
      i: '0',
      moved: false,
      static: false,
    },
    {
      w: 4,
      h: 9,
      x: 0,
      y: 10,
      i: '1',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 14,
      x: 4,
      y: 22,
      i: '2',
      moved: false,
      static: false,
    },
    {
      w: 4,
      h: 9,
      x: 0,
      y: 19,
      i: '3',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 12,
      x: 4,
      y: 10,
      i: '4',
      moved: false,
      static: false,
    },
    {
      w: 5,
      h: 10,
      x: 5,
      y: 0,
      i: '5',
      moved: false,
      static: false,
    },
    {
      w: 4,
      h: 8,
      x: 0,
      y: 28,
      i: '6',
      moved: false,
      static: false,
    },
  ],
  sm: [
    {
      w: 6,
      h: 10,
      x: 0,
      y: 0,
      i: '0',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 10,
      x: 0,
      y: 31,
      i: '1',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 12,
      x: 0,
      y: 41,
      i: '2',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 10,
      x: 0,
      y: 53,
      i: '3',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 11,
      x: 0,
      y: 20,
      i: '4',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 10,
      x: 0,
      y: 10,
      i: '5',
      moved: false,
      static: false,
    },
    {
      w: 6,
      h: 10,
      x: 0,
      y: 63,
      i: '6',
      moved: false,
      static: false,
    },
  ],
};

const WIDGETS = [
  { title: '공급현황', Component: PiCareSupplyTable },
  { title: '참여도 / 수행도', Component: PiCarePerformanceWidget },
  { title: '훈련 이용 방식', Component: PiCareBarChartUsage },
  { title: '주 사용 훈련', Component: PiCarePieChartUsage },
  { title: '시간대별 사용 패턴', Component: PiCareBarChartHourly },
  { title: '일별 사용량', Component: PiCareLineChartUsage },
  { title: '감정 분포', Component: PiCarePieChartEmotion },
];

const PicarePage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { managerId } = useParams();
  const { width } = useWindowSize();
  const [mounted, setMounted] = useState(false);
  const [isRunning, toggleIsRunning] = useBoolean(true);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const { loading, hwList } = useSelector((state) => state.picareDashboard);
  const isAnyLoading = Object.values(loading).some(Boolean);

  const handlePrint = () => {
    if (hwList?.length) {
      const institution = hwList[0].institution || hwList[0].agency || '';
      const subUsers = hwList
        .flatMap((hw) => hw.subUsers || [])
        .filter((u) => u.nickName);
      setPreviewData({
        ...DEFAULT_REPORT_DATA,
        header: { ...DEFAULT_REPORT_DATA.header, centerName: institution },
        recipients: subUsers.map((u) => ({ name: u.nickName })),
      });
    } else {
      setPreviewData(DEFAULT_REPORT_DATA);
    }
    setOpenPreview(true);
  };

  const CONTENT = WIDGETS.map(({ title, Component }, i) => (
    <Card key={i} variant="outlined" sx={{ height: 'inherit' }}>
      <CardContent sx={{ height: 'calc(100% - 2rem)', padding: '1rem' }}>
        <DragCardHeader variant="subtitle2">{title}</DragCardHeader>
        <Component />
      </CardContent>
    </Card>
  ));

  const getLayout = (v) =>
    (LAYOUT[v] || LAYOUT.sm).map((item, i) => ({
      ...item,
      content: CONTENT[i],
    }));

  const getBreakPoint = () => {
    const idx = Object.values(theme.breakpoints.values).reduce(
      (val, cur, i) => (width > cur ? i : val),
      -1,
    );
    return ['xxs', 'xs', 'sm', 'md', 'lg'][idx];
  };

  useInterval(
    () => {
      dispatch(fetchHwList());
      dispatch(fetchAllCharts());
    },
    isRunning ? 15 * 60 * 1000 : null,
  );

  useLifecycles(
    () => {
      setMounted(true);
      dispatch(setManagerId(managerId));
      dispatch(fetchHwList());
      dispatch(fetchAllCharts());
    },
    () => {
      setMounted(false);
      toggleIsRunning();
    },
  );

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h5" color="secondary" fontWeight="bold">
          piCare 관제 시스템
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isAnyLoading && <CircularProgress size={20} color="secondary" />}
          <Button
            onClick={handlePrint}
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<PrintIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            프린트
          </Button>
        </Box>
      </Box>
      {openPreview && (
        <PiCareReportPreview
          isOpen={openPreview}
          data={previewData}
          onClose={() => setOpenPreview(false)}
        />
      )}
      <ResponsiveReactGridLayout
        className="layout"
        rowHeight={30}
        cols={{ lg: 16, md: 10, sm: 6, xs: 4, xxs: 2 }}
        layouts={{
          lg: getLayout('lg'),
          md: getLayout('md'),
          sm: getLayout('sm'),
        }}
        measureBeforeMount={false}
        preventCollision={false}
        useCSSTransforms={mounted}
        compactType="vertical"
        // eslint-disable-next-line no-unused-vars
        onLayoutChange={(layout, layouts) => {
          // localStorage.setItem('picare-layout', JSON.stringify(layouts));
        }}
      >
        {getLayout(getBreakPoint()).map(({ w, h, x, y, i, content }) => (
          <div key={i} w={w} h={h} x={x} y={y}>
            {content}
          </div>
        ))}
      </ResponsiveReactGridLayout>
    </>
  );
};

export default PicarePage;
